import { useState, useEffect, useRef } from 'react';

interface UseTypewriterOptions {
  text: string;
  typingSpeed?: number;
  deletingSpeed?: number;
  pauseBeforeDelete?: number;
  pauseBeforeType?: number;
}

export function useTypewriter({
  text,
  typingSpeed = 60,
  deletingSpeed = 30,
  pauseBeforeDelete = 2000,
  pauseBeforeType = 500,
}: UseTypewriterOptions) {
  const [displayed, setDisplayed] = useState('');
  const [isTyping, setIsTyping] = useState(true);
  const indexRef = useRef(0);
  const phaseRef = useRef<'typing' | 'paused' | 'deleting' | 'waiting'>('typing');

  useEffect(() => {
    indexRef.current = 0;
    phaseRef.current = 'typing';
    setDisplayed('');
    setIsTyping(true);
  }, [text]);

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout>;

    const tick = () => {
      switch (phaseRef.current) {
        case 'typing':
          if (indexRef.current < text.length) {
            indexRef.current++;
            setDisplayed(text.slice(0, indexRef.current));
            timeout = setTimeout(tick, typingSpeed);
          } else {
            phaseRef.current = 'paused';
            setIsTyping(false);
            timeout = setTimeout(tick, pauseBeforeDelete);
          }
          break;
        case 'paused':
          phaseRef.current = 'deleting';
          setIsTyping(true);
          timeout = setTimeout(tick, deletingSpeed);
          break;
        case 'deleting':
          if (indexRef.current > 0) {
            indexRef.current--;
            setDisplayed(text.slice(0, indexRef.current));
            timeout = setTimeout(tick, deletingSpeed);
          } else {
            phaseRef.current = 'waiting';
            timeout = setTimeout(tick, pauseBeforeType);
          }
          break;
        case 'waiting':
          phaseRef.current = 'typing';
          timeout = setTimeout(tick, typingSpeed);
          break;
      }
    };

    timeout = setTimeout(tick, typingSpeed);
    return () => clearTimeout(timeout);
  }, [text, typingSpeed, deletingSpeed, pauseBeforeDelete, pauseBeforeType]);

  return { displayed, isTyping };
}
